/**
 * Platform-based client router: URLPattern for matching + History API for
 * navigation. Falls back to a tiny path matcher where URLPattern is absent.
 */
export type RouteName =
  "stream" | "feed" | "search" | "kept" | "feeds" | "rules" | "ruleActivity";

export interface RouteMatch {
  name: RouteName;
  params: Record<string, string>;
}

interface Matcher {
  name: RouteName;
  match(pathname: string): Record<string, string> | null;
}

const patterns: ReadonlyArray<{ name: RouteName; path: string }> = [
  { name: "stream", path: "/" },
  { name: "feed", path: "/feed/:id" },
  { name: "search", path: "/search" },
  { name: "kept", path: "/kept" },
  { name: "feeds", path: "/feeds" },
  { name: "rules", path: "/rules" },
  { name: "ruleActivity", path: "/rules/:id/activity" },
];

const hasURLPattern =
  "URLPattern" in globalThis && typeof URLPattern === "function";

const matchers: Matcher[] = patterns.map(({ name, path }) => {
  if (hasURLPattern) {
    const pattern = new URLPattern({ pathname: path });
    return {
      name,
      match(pathname) {
        const result = pattern.exec({ pathname });
        if (!result) return null;
        const params: Record<string, string> = {};
        for (const [key, value] of Object.entries(result.pathname.groups)) {
          if (value !== undefined) params[key] = value;
        }
        return params;
      },
    };
  }
  // Fallback: compile ":param" segments into a capturing regex.
  const keys: string[] = [];
  const source = path
    .split("/")
    .map((seg) => {
      if (seg.startsWith(":")) {
        keys.push(seg.slice(1));
        return "([^/]+)";
      }
      return seg.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    })
    .join("/");
  const regex = new RegExp(`^${source}/?$`);
  return {
    name,
    match(pathname) {
      const m = regex.exec(pathname);
      if (!m) return null;
      const params: Record<string, string> = {};
      keys.forEach((key, i) => {
        params[key] = decodeURIComponent(m[i + 1] ?? "");
      });
      return params;
    },
  };
});

export function matchRoute(pathname: string): RouteMatch {
  for (const matcher of matchers) {
    const params = matcher.match(pathname);
    if (params) return { name: matcher.name, params };
  }
  return { name: "stream", params: {} };
}

/** Push a new path and notify listeners (History API + custom event). */
export function navigate(path: string): void {
  if (path === window.location.pathname + window.location.search) return;
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/** Subscribe to route changes; returns an unsubscribe function. */
export function onRouteChange(handler: () => void): () => void {
  window.addEventListener("popstate", handler);
  return () => window.removeEventListener("popstate", handler);
}
